"use client";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { platformFetch } from "./browser";
import { useCurrentTier } from "./incentives";
import { useIdentity } from "./session";
import { toBets, toSportsbookBets, toTransfers } from "./mappers";
import { PAGE_LIMIT_MAX_100, clampLimit, fromCursor, type PageParam } from "./paginate";

/* Player statistics are public: `/accounts/{id}/statistics` needs no session,
   which is what lets the live table's hover card show a real player's real
   numbers to a signed-out visitor. */

export type AccountStatistics = {
  nickname: string;
  createdAt: string;
  decimals: number;
  significantDecimals: number;
  transactions: number;
  bets: number;
  wagered: string;
  rewards: string;
  isHidden: boolean;
  /* Not served today. The kit's player card shows a tier icon and name under
     the nickname, and nothing public says another player's tier —
     docs/platform-changes.md §8 asks for it. Typed here so the card reads it
     the day the platform sends it, without a change on this side. */
  tier?: { id: string; name: string; imageUrl: string | null } | null;
};

export type AccountTier = { name: string; imageUrl: string | null };

/* The tier to draw on a player's card, from whichever source can answer:
   the statistics payload when the platform includes one, else — only for
   the signed-in player looking at their own card — their current tier from
   the session. Anyone else's tier is unknown, and the card says nothing
   rather than guessing. */
export const useAccountTier = (
  accountId: string | undefined,
  statistics: AccountStatistics | undefined,
): AccountTier | null => {
  const { identity } = useIdentity();
  const isSelf = Boolean(accountId && identity?.id === accountId);
  const own = useCurrentTier(isSelf);

  if (statistics?.tier) return { name: statistics.tier.name, imageUrl: statistics.tier.imageUrl };
  if (isSelf && own.data) return { name: own.data.name, imageUrl: own.data.imageUrl };
  return null;
};

/* Fetched on first hover, not per row — a table of 25 bets would otherwise fire
   25 requests nobody asked for. Cached long, since lifetime totals barely move. */
export const useAccountStatistics = (accountId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ["account", accountId, "statistics"],
    enabled: enabled && Boolean(accountId),
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () =>
      (await platformFetch<{ data: AccountStatistics }>(`accounts/${accountId}/statistics`)).data,
  });

type AccountBetRow = Parameters<typeof toBets>[0][number];
type AccountTransferRow = Parameters<typeof toTransfers>[0][number];
type AccountSportsbookBetRow = Parameters<typeof toSportsbookBets>[0][number];

type AccountBetsEnvelope = { data: AccountBetRow[]; next?: string | null };
type AccountTransfersEnvelope = { data: AccountTransferRow[]; next?: string | null };
type AccountSportsbookBetsEnvelope = { data: AccountSportsbookBetRow[]; next?: string | null };

export const useAccountBets = (enabled: boolean, { limit = 25 } = {}) => {
  const query = useInfiniteQuery({
    queryKey: ["account", "bets", limit],
    enabled,
    staleTime: 30_000,
    initialPageParam: undefined as PageParam | undefined,
    queryFn: ({ pageParam }) =>
      platformFetch<AccountBetsEnvelope>("account/bets/casino", {
        limit: clampLimit(limit, PAGE_LIMIT_MAX_100),
        order: "desc",
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => fromCursor(lastPage).nextPageParam,
  });

  return {
    ...query,
    bets: toBets((query.data?.pages ?? []).flatMap((page) => page.data), Date.now()),
  };
};

export const useAccountTransfers = (enabled: boolean, { limit = 25 } = {}) => {
  const query = useInfiniteQuery({
    queryKey: ["account", "transfers", limit],
    enabled,
    staleTime: 30_000,
    initialPageParam: undefined as PageParam | undefined,
    queryFn: ({ pageParam }) =>
      platformFetch<AccountTransfersEnvelope>("account/transfer-transactions", {
        limit: clampLimit(limit, PAGE_LIMIT_MAX_100),
        order: "desc",
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => fromCursor(lastPage).nextPageParam,
  });

  return {
    ...query,
    transfers: toTransfers((query.data?.pages ?? []).flatMap((page) => page.data), Date.now()),
  };
};

export const useAccountSportsbookBets = (enabled: boolean, { limit = 25 } = {}) => {
  const query = useInfiniteQuery({
    queryKey: ["account", "bets", "sportsbook", limit],
    enabled,
    staleTime: 30_000,
    initialPageParam: undefined as PageParam | undefined,
    queryFn: ({ pageParam }) =>
      platformFetch<AccountSportsbookBetsEnvelope>("account/bets/sportsbook", {
        limit: clampLimit(limit, PAGE_LIMIT_MAX_100),
        order: "desc",
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => fromCursor(lastPage).nextPageParam,
  });

  return {
    ...query,
    bets: toSportsbookBets((query.data?.pages ?? []).flatMap((page) => page.data), Date.now()),
  };
};

export type EarnOnLosses = {
  totalLosses: string;
  totalWagered: string;
  totalCommission: string;
  currentMonthLosses: string;
  currentMonthWagered: string;
  currentMonthCommission: string;
  codes: {
    month: string;
    code: string;
    signups: string;
    commission: string;
    commissionRate: string;
    numberOfFirstTimeDeposits: string;
  }[];
};

export const useEarnOnLosses = (accountId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ["account", accountId, "earn-on-losses"],
    enabled: enabled && Boolean(accountId),
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () =>
      (await platformFetch<{ data: EarnOnLosses }>(`accounts/${accountId}/earn-on-losses`)).data,
  });
